<template>
  <div>
    <fade-transition :duration="250" mode="out-in">

      <landing
        v-if="getComponentId('landing') === activeComponent.id"
        @continue="incrementActiveComponent">
      </landing>

      <questionnaire
        v-if="getComponentId('questionnaire') === activeComponent.id"
        :questionnaire="questionnaire"
        :answers.sync="form.data.answers"
        :initialQuestionIndex="views.questionnaire.initialQuestionIndex"
        @back="form.data.answers = []; decrementActiveComponent()"
        @continue="incrementActiveComponent">
      </questionnaire>

      <hearing-test-configuration
        v-if="getComponentId('hearingTestConfiguration') === activeComponent.id"
        :user.sync="form.data.user"
        :headphone.sync="form.data.headphone"
        @continue="incrementActiveComponent">
      </hearing-test-configuration>

      <hearing-test-calibration
        v-if="getComponentId('hearingTestCalibration') === activeComponent.id"
        :handler="handler"
        @continue="incrementActiveComponent">
      </hearing-test-calibration>

      <hearing-test-numbers
        v-if="getComponentId('hearingTestNumbers') === activeComponent.id"
        :handler="handler"
        :ht_numbers.sync="form.data.ht_numbers"
        @continue="incrementActiveComponent">
      </hearing-test-numbers>

      <hearing-test-words
        v-if="getComponentId('hearingTestWords') === activeComponent.id"
        :handler="handler"
        :ht_words.sync="form.data.ht_words"
        @continue="incrementActiveComponent">
      </hearing-test-words>

      <hearing-test-results
        v-if="getComponentId('hearingTestResults') === activeComponent.id"
        :user.sync="form.data.user"
        :errors="form.errors"
        :hasHearingLoss="hasHearingLoss"
        @continue="incrementActiveComponent">
      </hearing-test-results>

      <hearing-test-player
        v-if="getComponentId('hearingTestPlayer') === activeComponent.id"
        :handler="handler"
        :ht.sync="form.data.ht"
        @continue="incrementActiveComponent">
      </hearing-test-player>

      <contact-request-form
        v-if="getComponentId('contactRequestForm') === activeComponent.id"
        :user.sync="form.data.user"
        :errors="form.errors"
        :hasHearingLoss="hasHearingLoss"
        :isSubmiting="isSubmiting"
        @continue="submit">
      </contact-request-form>

      <contact-request-form-no-results
        v-if="getComponentId('contactRequestFormNoResults') === activeComponent.id"
        :user.sync="form.data.user"
        :errors="form.errors"
        :hasHearingLoss="hasHearingLoss"
        :isSubmiting="isSubmiting"
        @continue="submit">
      </contact-request-form-no-results>

      <good-bye
        v-if="getComponentId('goodbye') === activeComponent.id">
      </good-bye>

      <good-bye-with-results
        v-if="getComponentId('goodbyeWithResults') === activeComponent.id"
        :hasHearingLoss="hasHearingLoss">
      </good-bye-with-results>

    </fade-transition>
  </div>
</template>
<script>
import Landing from '@/vue-components/Landing.vue'
import HearingTestCalibration from '@/vue-components/HearingTestCalibration.vue'
import HearingTestNumbers from '@/vue-components/HearingTestNumbers.vue'
import HearingTestWords from '@/vue-components/HearingTestWords.vue'
import HearingTestConfiguration from '@/vue-components/HearingTestConfiguration.vue'
import HearingTestPlayer from '@/vue-components/HearingTestPlayer.vue'
import Questionnaire from '@/vue-components/Questionnaire/Questionnaire.vue'
import HearingTestResults from '@/vue-components/HearingTestResults.vue'
import HearingTestHeader from '@/vue-components/HearingTestHeader.vue'
import HearingTestHandler from '@/HearingTestHandler'
import AppNavigationBar from '@/vue-components/AppNavigationBar.vue'
import ContactRequestForm from '@/vue-components/ContactRequestForm.vue'
import contactRequestFormNoResults from '@/vue-components/ContactRequestFormNoResults.vue'
import GoodBye from '@/vue-components/GoodBye.vue'
import GoodByeWithResults from '@/vue-components/GoodByeWithResults.vue'
import FadeTransition from '@/vue-components/Utils/FadeTransition.vue'
import Errors from '@/utils/Errors'
import { LeadsService } from '@/services/LeadsService'
import { QuestionnaireService } from '@/services/LeadsService'
import CONSTANTS from '@/utils/config'
import { analyticsIds, pixelIds } from '@/utils/gtag'

const validParams = ['lang', 'source', 'entity', 'aud_provider', 'srv', 'flow', 'redirect', 'ref_id', 'analytics']
const RECAPTCHA_TOKEN = '6LeMKL4ZAAAAAEWGYj2dzMRmuTNg8hPBnCRPQsPS'

export default {
  components: {
    AppNavigationBar,
    HearingTestHeader,
    Landing,
    HearingTestConfiguration,
    HearingTestCalibration,
    HearingTestNumbers,
    HearingTestWords,
    HearingTestPlayer,
    Questionnaire,
    HearingTestResults,
    ContactRequestForm,
    contactRequestFormNoResults,
    GoodBye,
    GoodByeWithResults,
    FadeTransition,
  },
  data () {
    return {
      activeViewIndex: 0,
      activeViewOrder: null,
      componentsUrls: [
        { name: 'landing', url: '/' },
        { name: 'questionnaire', url: '/questionnaire' },
        { name: 'hearingTestConfiguration', url: '/hearing-test-configuration' },
        { name: 'hearingTestCalibration', url: '/hearing-test-calibration' },
        { name: 'hearingTestPlayer', url: '/tonal' },
        { name: 'hearingTestNumbers', url: '/numbers'},
        { name: 'hearingTestWords', url: '/words'},
        { name: 'contactRequestForm', url: '/contact-form' },
        { name: 'contactRequestFormNoResults', url: '/contact-form' },
        { name: 'goodbye', url: '/submitted' }
      ],
      viewsA: [
        {
          id: 1,
          name: 'landing',
          title: this.$t('headers.hearing_test'),
          url: '/'
        },
        {
          id: 2,
          name: 'questionnaire',
          title: this.$t('headers.questionnaire'),
          url: '/questionnaire'
        },
        {
          id: 3,
          name: 'hearingTestConfiguration',
          title: this.$t('headers.hearing_test'),
          url: '/hearing-test-configuration'
        },
        {
          id: 4,
          name: 'hearingTestCalibration',
          title: this.$t('headers.hearing_test'),
          url: '/hearing-test-calibration'
        },
        {
          id: 5,
          name: 'hearingTestPlayer',
          title: this.$t('headers.hearing_test'),
          url: '/hearing-test'
        },
        {
          id: 6,
          name: 'contactRequestForm',
          title: this.$t('headers.hearing_test'),
          url: '/contact-form'
        },
        {
          id: 7,
          name: 'goodbye',
          title: this.$t('headers.goodbye'),
          url: '/submitted'
        }
      ],
      viewsB: [// Tonal Test (2)
        {id: 1, name: 'landing', title: this.$t('headers.hearing_test')},
        {id: 2, name: 'hearingTestConfiguration', title: this.$t('headers.hearing_test')},
        // instruments calibration removed for now
        // {id: 3, name: 'hearingTestCalibration', title: this.$t('headers.hearing_test')},
        {id: 3, name: 'hearingTestPlayer', title: this.$t('headers.hearing_test')},
        {id: 4, name: 'questionnaire', title: this.$t('headers.questionnaire')},
        {id: 5, name: 'contactRequestFormNoResults', title: this.$t('headers.hearing_test')},
        {id: 6, name: 'goodbye', title: this.$t('headers.goodbye')}
      ],
      viewsC: [// Numbers Test (3)
        {id: 1, name: 'landing', title: this.$t('headers.hearing_test')},
        {id: 2, name: 'questionnaire', title: this.$t('headers.questionnaire')},
        {id: 3, name: 'hearingTestConfiguration', title: this.$t('headers.hearing_test')},
        {id: 4, name: 'hearingTestNumbers', title: this.$t('headers.hearing_test')},
        {id: 5, name: 'contactRequestFormNoResults', title: this.$t('headers.hearing_test')},
        {id: 6, name: 'goodbye', title: this.$t('headers.goodbye')}
      ],
      viewsD: [// Words Test (4)
        {id: 1, name: 'landing', title: this.$t('headers.hearing_test')},
        {id: 2, name: 'questionnaire', title: this.$t('headers.questionnaire')},
        {id: 3, name: 'hearingTestConfiguration', title: this.$t('headers.hearing_test')},
        {id: 4, name: 'hearingTestWords', title: this.$t('headers.hearing_test')},
        {id: 5, name: 'contactRequestFormNoResults', title: this.$t('headers.hearing_test')},
        {id: 6, name: 'goodbye', title: this.$t('headers.goodbye')}
      ],
      viewsE: [// Numbers + Words Test (5)
        {id: 1, name: 'landing', title: this.$t('headers.hearing_test')},
        {id: 2, name: 'questionnaire', title: this.$t('headers.questionnaire')},
        {id: 3, name: 'hearingTestConfiguration', title: this.$t('headers.hearing_test')},
        {id: 4, name: 'hearingTestNumbers', title: this.$t('headers.hearing_test')},
        {id: 5, name: 'hearingTestWords', title: this.$t('headers.hearing_test')},
        {id: 6, name: 'contactRequestFormNoResults', title: this.$t('headers.hearing_test')},
        {id: 7, name: 'goodbye', title: this.$t('headers.goodbye')}
      ],
      viewsF: [// Numbers + Words + Tonal Test (6)
        {id: 1, name: 'landing', title: this.$t('headers.hearing_test')},
        {id: 2, name: 'questionnaire', title: this.$t('headers.questionnaire')},
        {id: 3, name: 'hearingTestConfiguration', title: this.$t('headers.hearing_test')},
        {id: 4, name: 'hearingTestNumbers', title: this.$t('headers.hearing_test')},
        {id: 5, name: 'hearingTestWords', title: this.$t('headers.hearing_test')},
        {id: 6, name: 'hearingTestPlayer', title: this.$t('headers.hearing_test')},
        {id: 7, name: 'contactRequestFormNoResults', title: this.$t('headers.hearing_test')},
        {id: 8, name: 'goodbye', title: this.$t('headers.goodbye')}
      ],
      views: {
        landing: {
          id: 1,
          title: this.$t('headers.hearing_test')
        },
        questionnaire: {
          id: 2,
          initialQuestionIndex: 0,
          title: this.$t('headers.questionnaire')
        },
        hearingTestConfiguration: {
          id: 3,
          title: this.$t('headers.hearing_test')
        },
        hearingTestCalibration: {
          id: 4,
          title: this.$t('headers.hearing_test')
        },
        hearingTestPlayer: {
          id: 5,
          title: this.$t('headers.hearing_test')
        },
        hearingTestResults: {
          id: 6,
          title: this.$t('headers.hearing_test')
        },
        contactRequestForm: {
          id: 7,
          title: this.hasHearingLoss ? this.$t('results_stage.hearing_loss.has_loss') : this.$t('results_stage.hearing_loss.ok')
        },
        goodbye: {
          id: 8,
          title: this.$t('headers.goodbye')
        }
      },
      checkupA: [1, 2, 3, 4, 5, 6, 7, 8],
      checkupB: [1, 3, 4, 5, 2, 6, 7, 8],
      handler: null,
      form: {
        data: {
          user: {
            name: null,
            city: null,
            address_line_1: null,
            address_postal_code: null,
            address_city: null,
            email: null,
            phone: null,
            birthdate: null,
            gender: null,
            contact_request: false,
            marketing: false
          },
          answers: [],
          ht: null,
          ht_numbers: null,
          ht_words: null,
          headphone: null
        },
        errors: new Errors()
      },
      questionnaire: null,
      isSubmiting: false,
      srv: null
    }
  },
  created () {
    if (this.routeParams.hasOwnProperty('redirect')) {
      this.routeParams.redirect = decodeURIComponent (this.routeParams.redirect)
      if (!this.routeParams.redirect.startsWith('http')) this.routeParams.redirect = `http://${this.routeParams.redirect }`
    }
    if (this.routeParams.hasOwnProperty('lang') && (this.routeParams.lang === 'pt' || this.routeParams.lang === 'en')) this.$i18n.locale = this.routeParams.lang
    if (this.routeParams.hasOwnProperty('srv') && this.routeParams.srv !== '') this.srv = 'https://' + this.routeParams.srv + '.evollu.com'
    this.loadQuestions()
    if ('flow' in this.routeParams && this.routeParams.flow !== '') {
      this.routeParams.flow = decodeURIComponent (this.routeParams.flow)
      let sections = this.routeParams.flow.split(',').map((e)=>parseInt(e))
      let views = [
        {id: 1, name: 'landing', title: this.$t('headers.hearing_test')}
      ]
      let i = 2
      const index = sections.indexOf(1);
      if (index === 0) {
        views.push({id: i, name: 'questionnaire', title: this.$t('headers.questionnaire')})
        i++
        sections.splice(index, 1);
      }

      if (sections.indexOf(2) > -1 || sections.indexOf(3) > -1 || sections.indexOf(4) > -1) {
        views.push({id: i, name: 'hearingTestConfiguration', title: this.$t('headers.hearing_test')})
        i++
      }

      sections.forEach(section => {
        switch (section) {
        case 1:
          views.push({id: i, name: 'questionnaire', title: this.$t('headers.questionnaire')})
          i++
          break
        case 2:
          views.push({id: i, name: 'hearingTestNumbers', title: this.$t('headers.hearing_test')})
          i++
          break
        case 3:
          views.push({id: i, name: 'hearingTestWords', title: this.$t('headers.hearing_test')})
          i++
          break
        case 4:
          views.push({id: i, name: 'hearingTestPlayer', title: this.$t('headers.hearing_test')})
          i++
          break
        }
      })

      views.push({id: i, name: 'contactRequestFormNoResults', title: this.$t('headers.hearing_test')})
      i++
      views.push({id: i, name: 'goodbye', title: this.$t('headers.goodbye')})

      this.activeViewOrder = views
    } else {
      this.activeViewOrder =
      CONSTANTS.ORDER === 1 ? this.viewsA :
        CONSTANTS.ORDER === 2 ? this.viewsB :
          CONSTANTS.ORDER === 3 ? this.viewsC :
            CONSTANTS.ORDER === 4 ? this.viewsD :
              CONSTANTS.ORDER === 5 ? this.viewsE : this.viewsF
    }
    // console.log(this.routeParams);
  },
  methods: {
    decrementActiveComponent: function () {
      if (this.activeViewIndex - 1 >= 0) {
        this.activeViewIndex --
      }
    },
    incrementActiveComponent: function () {
      if (this.activeViewIndex + 1 <= this.activeViewOrder.length) {
        this.activeViewIndex ++
        window.scrollTo(0, 0)
      }
    },
    getComponentId: function (component) {
      let view = this.activeViewOrder.find(item => item.name === component)
      return view ? view.id : -1
    },
    getComponentUrl: function (component) {
      let comp = this.componentsUrls.find(item => item.name === component.name)
      return comp ? comp.url : '/' + component.name
    },
    getTestName: function () {
      let name = ''
      this.activeViewOrder.forEach(view => {
        if (view.name === 'hearingTestNumbers') name += 'tonal '
        if (view.name === 'hearingTestWords') name += 'words '
        if (view.name === 'hearingTestPlayer') name += 'numbers '
      })
      name += 'test ' + this.$i18n.locale
      return name
    },
    async submit() {
      if (!this.form.errors.any()) {
        let data = this.getFormData()
        this.isSubmiting = true

        const token = await this.recaptchaToken()

        data = { ...data, token }
        if (this.routeParams.hasOwnProperty('aud_provider') && !isNaN(parseInt(this.routeParams.aud_provider))) {
          data.aud_provider_id = parseInt(this.routeParams.aud_provider)
        }
        if (this.routeParams.hasOwnProperty('ref_id') && !isNaN(parseInt(this.routeParams.ref_id))) {
          data.ref_id = parseInt(this.routeParams.ref_id)
        }

        LeadsService.store(this.srv, data)
          .then(response => {
            // console.log(response.data.data)
            this.incrementActiveComponent()
            let label = this.getTestName()
            if (analyticsIds.length) {
              this.$gtag.event('submit', {
                'event_category': 'button',
                'event_label': label
              })
            }
            if (pixelIds.length) {
              fbq('track', 'Lead', {
                // content_name: 'teste_words',
                content_name: label,
                content_category: 'testes_auditivos',
              })
            }
          })
          .catch(errors => console.log(errors))
          .finally(() => {
            this.isSubmiting = false
            if (this.routeParams.hasOwnProperty('redirect')){
              window.location.href = this.routeParams.redirect
              setTimeout(window.location.href = this.routeParams.redirect,2000);
            }
          })
      }
    },
    recaptchaToken() {
      return new Promise((resolve) => {
        grecaptcha.ready(async () => {
          const token = await grecaptcha.execute(RECAPTCHA_TOKEN)
          resolve(token)
        })
      })
    },
    loadQuestions: function () {
      let locale = this.$i18n.locale.toUpperCase()
      let questionnaire_id = CONSTANTS.QUESTIONNAIRE.hasOwnProperty(locale) ? CONSTANTS.QUESTIONNAIRE[locale].ID : CONSTANTS.QUESTIONNAIRE.PT.ID
      QuestionnaireService.get(this.srv, questionnaire_id)
        .then(questionnaire => this.questionnaire = questionnaire)
        .catch(errors => console.log(errors))
    },
    getFormData: function () {
      return {
        version: '0.1',
        entity_id: this.routeParams.hasOwnProperty('entity') && !isNaN(parseInt(this.routeParams.entity)) ? parseInt(this.routeParams.entity) : CONSTANTS.LEAD.ENTITY_ID,
        source_id: this.routeParams.hasOwnProperty('source') && !isNaN(parseInt(this.routeParams.source)) ? parseInt(this.routeParams.source) : CONSTANTS.LEAD.SOURCE_ID,
        lang: this.routeParams.hasOwnProperty('lang') ? this.routeParams.lang : this.$i18n.locale,
        has_hearingLoss: this.hasHearingLoss,
        ... this.form.data.user,
        ht: this.form.data.ht ? this.getHearingTestStructure() : null,
        ht_numbers: this.form.data.ht_numbers ? {
          left: this.form.data.ht_numbers.left[0].lastStage,
          right: this.form.data.ht_numbers.right[0].lastStage,
          device: this.browserInfo(),
          version: '0.1',
          headphones_type: this.form.data.headphone.name
        } : null,
        ht_words: this.form.data.ht_words ? {
          left: this.form.data.ht_words.left[0].lastStage,
          right: this.form.data.ht_words.right[0].lastStage,
          device: this.browserInfo(),
          version: '0.1',
          headphones_type: this.form.data.headphone.name
        } : null,
        questionnaire: this.form.data.answers.map(answer => answer.id)
      }
    },
    getHearingTestStructure: function () {
      return {
        hl_left: this.form.data.ht.left,
        hl_right: this.form.data.ht.right,
        result: this.hearingTestFormatter(this.form.data.ht.results),
        device: this.browserInfo(),
        headphones_type: this.form.data.headphone.name
      }
    },
    hearingTestFormatter: function (ht) {
      let frequencies = ht[Object.keys(ht)[0]].map(item => item.frequency)
      let leftValues = ht['left'].map(item => item.amplitude)
      let rightValues = ht['right'].map(item => item.amplitude)

      let buffer = ''

      buffer += 'F\t' + frequencies.join('\t') + '\n'
      buffer += 'R\t' + rightValues.join('\t') + '\n'
      buffer += 'L\t' + leftValues.join('\t')

      return buffer
    },
    browserInfo: function () {
      let os = 'unknown OS'
      if (this.$browserDetect.meta.ua.indexOf('Win') != -1) os = 'Windows OS'
      if (this.$browserDetect.meta.ua.indexOf('Mac') != -1) os = 'MacOS'
      if (this.$browserDetect.meta.ua.indexOf('X11') != -1) os = 'UNIX OS'
      if (this.$browserDetect.meta.ua.indexOf('Linux') != -1) os = 'Linux OS'
      return this.$browserDetect.meta.name + ' (version: ' + this.$browserDetect.meta.version + ') / ' + os
    }
  },
  computed: {
    activeComponent: function () {
      if (this.activeViewOrder) {
        return this.activeViewOrder[this.activeViewIndex]
      }
    },
    hasHearingLoss: function () {
      if (this.form.data.answers) {
        let min_loss_value = CONSTANTS.QUESTIONNAIRE.hasOwnProperty(this.$i18n.locale.toUpperCase()) ? CONSTANTS.QUESTIONNAIRE[this.$i18n.locale.toUpperCase()].MIN_LOSS_VALUE : CONSTANTS.QUESTIONNAIRE.PT.MIN_LOSS_VALUE
        if (this.form.data.answers.reduce((accumulator, currentAnswer) => accumulator + currentAnswer.weight, 0) > min_loss_value) {
          return true
        }
      }
      if (this.form.data.ht) {
        return Object.keys(this.form.data.ht.results).filter(key => {
          return this.form.data.ht.results[key].some(result => result.amplitude >= CONSTANTS.HEARING_TEST.MIN_LOSS_INTERVAL)
        }).length > 0
      }

      if (this.form.data.ht_numbers) {
        const hasHL = (result) => result.lastStage !== 'end'
        return Object.keys(this.form.data.ht_numbers).filter(key => {
          return this.form.data.ht_numbers[key].some(hasHL)
        }).length > 0
      }

      if (this.form.data.ht_words) {
        const hasHL = (result) => result.lastStage !== 'end'
        return Object.keys(this.form.data.ht_words).filter(key => {
          return this.form.data.ht_words[key].some(hasHL)
        }).length > 0
      }

      return false
    },
    routeParams: function () {
      let params = {}
      // If you are wondering why the following code is necessary, you should ask Wordpress developers why they are lazy people :)
      let location = window.location.href
      console.log('window.location', window.location)
      console.log('href', location)
      location = location.split(`${window.location.origin}${window.location.pathname}`)
      console.log('after split', location)

      if (location.length >= 1 && location[1].startsWith('?')) {
        console.log('inside')
        location = location[1].substring(1)
        if (location.includes('#038;')) {
          location = location.replace(/#038;/gm, '&')
        }
        console.log('before decode')
        location = decodeURIComponent(location)
        console.log('after decode')

        let urlParams = location.split('&').filter(v => v)
        if (urlParams.length > 0) {
          urlParams.forEach(urlParam => {

            let sections = urlParam.split('=')
            console.log(urlParam, )
            if (validParams.includes(sections[0])) {
              params[sections[0].toLowerCase()] = sections[1].toLowerCase()
            }
          })
        }
      }

      console.log('Applying the following url params')
      console.log(params)

      return params
    }
  },
  watch: {
    activeComponent: {
      handler: function (component) {
        if (component) {
          let url = this.getComponentUrl(component)
          if (analyticsIds.length) {
            // this.$ga.page(`/hearing-test${component.url}`)
            this.$gtag.pageview({
              page_path: `/hearing-test${url}`,
            })
          }
        }
      }
    },
    'form.data.headphone': {
      handler: function (value) {
        if (value) {
          this.handler = new HearingTestHandler(value, this.$i18n.locale)
        }
      },
      deep: true
    }
  }
}
</script>
